/**
 * A type that can be null or undefined.
 */
export type nil = null | undefined;

/**
 * A simple pair of typed value and label.
 */
export interface ValueLabelPair<T> {
    /**
     * The value of an item.
     */
    value: T;
    /**
     * The display label for an item.
     */
    label: string;
}

/**
 * Generic interface for a simple object used as a map of type T, keyed by string.
 */
export interface ObjectMap<T = any> {
    [name: string]: T;
}

/**
 * Generic interface for a simple object used as a map of type T, keyed by number.
 */
export interface ObjectMapByNumber<T = any> {
    [name: number]: T;
}

/**
 * Extracts keys of type T that are assignable to string.
 * (omits number and symbol key types).
 */
export type StringKeyOf<T> = Extract<keyof T, string>;

/**
 * Remove types from T that are not assignable to U
 */
export type Filter<T, U> = T extends U ? T : never;

/**
 * Removes null from type T.
 */
export type NonNull<T> = Exclude<T, null>;

/**
 * Removes undefined from type T.
 */
export type NonUndefined<T> = Exclude<T, undefined>;

/**
 * Removes null and undefined from type T.
 */
export type NonNil<T> = Exclude<T, nil>;

/**
 * Utility type that modifies an object type to allow `undefined` on all properties.
 */
export type WithUndefinedProperties<T extends Record<string, never>> = {
    [P in keyof T]: T[P] | undefined;
};

/**
 * Extracts the type of a React component's props from the React component type T.
 * Usage example: type MyComponentProps = ExtractReactPropsType<typeof MyComponent>;
 */
export type ExtractReactPropsType<T> =
    T extends React.ComponentType<infer P> ? P : T extends React.Component<infer P> ? P : never;

/**
 * Enum defining all possible url strings for the Circular Templates API environments.
 */
export enum CircularTemplatesApiEnvironments {
    /**
     * Url for the production environment.
     */
    PRODUCTION = "https://pro.adadapted.com/api",
    /**
     * Url for the production mirror environment.
     */
    PRODUCTION_MIRROR = "https://pro.adadapted.dev/api",
    /**
     * Url for the staging environment.
     */
    STAGING = "https://stg.adadapted.dev/api",
    /**
     * Url for the development environment.
     */
    DEVELOPMENT = "https://dev.adadapted.dev/api",
}

/**
 * Enum defining all possible url strings for the Events API environments.
 */
export enum EventsApiEnvironments {
    /**
     * Url for the production environment.
     */
    PRODUCTION = "https://telemetry.adadapted.com/td.production.circular_events",
    /**
     * Url for the production mirror environment.
     */
    PRODUCTION_MIRROR = "https://telemetry.adadapted.dev/td.sandbox.circular_events",
    /**
     * Url for the staging environment.
     */
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    STAGING = "https://telemetry.adadapted.dev/td.sandbox.circular_events",
    /**
     * Url for the development environment.
     */
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    DEVELOPMENT = "https://telemetry.adadapted.dev/td.sandbox.circular_events",
}
